/**
 * The column a board stacks by.
 *
 * A single select and nothing else, because the stacks *are* its options -- a
 * board grouped by a column with no fixed set of values would have a column
 * per distinct cell.
 */
import { Columns3 } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import type { Field, ViewOf } from '@/api/types'
import { Button } from '@/shared/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/shared/ui/dropdown-menu'
import { singleSelectFields } from '../views/column-choices'
import { FieldChoice } from './field-choice'
import { useViewSettings } from './view-settings-mutations'

export function StackByMenu({
  tableId,
  view,
  fields,
}: {
  tableId: number
  view: ViewOf<'board'>
  fields: readonly Field[]
}) {
  const { t } = useTranslation()
  const { choose } = useViewSettings(tableId, view.id)
  const candidates = singleSelectFields(fields)

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="sm" className="gap-1.5">
          <Columns3 className="size-4 opacity-70" />
          {t('view.stackBy')}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-56">
        {candidates.length === 0 ? (
          <DropdownMenuItem disabled>{t('view.stackByNoFields')}</DropdownMenuItem>
        ) : (
          <FieldChoice
            label={t('view.stackByField')}
            chosen={view.single_select_field ?? null}
            candidates={candidates}
            onChoose={(id) => {
              choose({ single_select_field: id })
            }}
          />
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
